import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { ModalSeatingDetails, SeatingChartGrid } from 'features/seating-chart';
import { AlertMessage, EventDashboardDetails } from 'shared/components';
import * as taskActions from 'features/task-management/duck';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as seatingChartActions from 'features/seating-chart/duck';
import { getAllStudents } from 'features/seating-chart/selectors';
import { isEmpty, filter, find, some, sumBy } from 'lodash';
import { isRoomOnline, str2ClassName, getRoomTimingName } from 'shared/helpers';
import PencilIcon from 'assets/svgs/pencilIcon24.svg';
const ALERT_MESSAGE_TIME = 2000;

const seatingDetails = {
  seatingRowCount: '',
  seatingColumnCount: '',
  writingSurfaceWidth: '',
  writingSurfaceDepth: '',
  distanceBetweenShoulders: '',
  distanceBetweenHeads: '',
};

export class SeatingChartDashboard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      modalEditSeatsOpen: false,
      completedSeatingDetailsFlag: true,
      showAlertMessage: false,
      alertMessage: '',
      viewMaterialType: 0,
    };

    this.toggleEditSeatModal = this.toggleEditSeatModal.bind(this);
    this.removeAlertMessage = this.removeAlertMessage.bind(this);
    this.handleChangeMaterialType = this.handleChangeMaterialType.bind(this);
    this.seatingChart = React.createRef();
  }

  // Flag-consumption pattern per STATEDIST-11343 (see
  // StaffAndRoomsContainer.isScrollRestoreEnabled()): StaticService.getConfigValues() ->
  // staticDuck.js (GET_CONFIG_VALUES) -> state.statics.configValues -> looked up here.
  isRoomSetupRedesignEnabled() {
    const entry = (this.props.configValues || []).find(
      (configValue) => configValue.code === 'room_setup_redesign_enabled'
    );
    return entry?.value === 'true';
  }

  // STATEDIST-11665: under the redesign, RoomApiImpl.create() always nulls
  // writingSurfaceWidth/writingSurfaceDepth/distanceBetweenShoulders/distanceBetweenHeads —
  // the legacy fields the original check looked at — so that check was permanently true
  // ("incomplete") regardless of whether rows/columns/seating were actually set. When the
  // flag is on, only those redesign-relevant fields determine completeness; the legacy
  // fields (still genuinely required/collected when the flag is off) keep the original check.
  isSeatingDetailsComplete(room) {
    if (this.isRoomSetupRedesignEnabled()) {
      return !!(room.seatingRowCount && room.seatingColumnCount && !isEmpty(room.seating));
    }
    return !Object.keys(seatingDetails).some((key) => !room[key]);
  }

  componentDidMount() {
    const { actions, eventId, roomId, tcEvent, room } = this.props;

    actions.getRoomStatus(roomId);
    if (isEmpty(tcEvent)) {
      actions.getTcEvent(eventId);
    }
    actions.getStudentsList(roomId);
    if (!this.isSeatingDetailsComplete(room)) {
      this.setState({
        modalEditSeatsOpen: true,
        completedSeatingDetailsFlag: false,
      });
    }
  }

  componentDidUpdate(prevProps) {
    const alertMessage = sessionStorage.getItem('successSavedMessage');

    if (alertMessage) {
      sessionStorage.removeItem('successSavedMessage');
      this.setState({
        showAlertMessage: true,
        alertMessage: alertMessage,
      });
    }

    Array.from(document.querySelectorAll('[data-bs-toggle="popover"]')).forEach((el) =>
      bootstrap.Popover.getOrCreateInstance(el).enable()
    );

    if (
      this.props.location.state?.successNotif &&
      prevProps.location.state?.successNotif !== this.props.location.state.successNotif
    ) {
      this.setState({
        showAlertMessage: true,
        alertMessage: this.props.location.state.successNotif,
      });
      globalThis.history.replaceState(null, ''); // Remove message on page reload
    }
  }

  handleChangeMaterialType(e) {
    this.setState({
      viewMaterialType: e.target.value,
    });
  }

  // STATEDIST-11665: closing the popup used to only flip modalEditSeatsOpen, never
  // recomputing completedSeatingDetailsFlag — so the grid/booklets (both gated on that
  // flag, see render()) stayed hidden after close even once seating details were genuinely
  // complete (e.g. right after saving from the popup). Recompute fresh from the current
  // room prop on close only — opening leaves it alone, and a close without saving (still
  // genuinely incomplete) correctly keeps the grid/booklets hidden rather than forcing them
  // to show for a room SeatingChartGrid can't render (no rows/columns yet).
  toggleEditSeatModal() {
    this.setState((prevState) => {
      const closing = prevState.modalEditSeatsOpen;
      return {
        modalEditSeatsOpen: !prevState.modalEditSeatsOpen,
        completedSeatingDetailsFlag: closing
          ? this.isSeatingDetailsComplete(this.props.room)
          : prevState.completedSeatingDetailsFlag,
      };
    });
  }

  removeAlertMessage() {
    this.setState({
      showAlertMessage: false,
      alertMessage: '',
    });
  }

  render() {
    const {
      allStudents,
      room,
      roomTypeMaterialTypes,
      roomsRanges,
      roomStatus,
      regSummaries,
      roomId,
      eventId,
      tcEvent,
    } = this.props;

    const {
      modalEditSeatsOpen,
      showAlertMessage,
      alertMessage,
      viewMaterialType,
      completedSeatingDetailsFlag,
    } = this.state;

    const gridProps = {
      type: 'material-type',
    };

    if (viewMaterialType) {
      gridProps.material = find(roomTypeMaterialTypes, {
        id: +viewMaterialType,
      });
    }

    const btnStyle = {};
    if (room.seatingRowCount <= 6) {
      btnStyle.top = 20 + (room.seatingRowCount * (80 + 2 * 8)) / 2 - 64 / 2 + 'px';
    }

    const basePath = `/test-event/${eventId}/room/${roomId}/seating-chart`;
    const btnAction = tcEvent.equatingFlag ? 'equating' : 'assign';

    return (
      <div className="seating-chart-container">
        <div className="title-container mb-30">
          <h4>{this.props.t('seatingChart.title')}</h4>
          <EventDashboardDetails
            date={tcEvent?.testingEvent?.name || ''}
            name={roomStatus.testCenterName}>
            <span>{roomStatus.locationName}</span>
            {roomStatus.locationArea && roomStatus.locationArea.length > 0 && (
              <span>, {roomStatus.locationArea}</span>
            )}
          </EventDashboardDetails>

          <p className="text-strong">{room.name}</p>
          <div className="labels-container">
            <span>
              {room.roomType
                ? room.roomType.name
                : this.props.t('testCenter.roomDashboard.noTypeLabel')}
            </span>
            {room.timing ? <span>{getRoomTimingName(room.timing, this.props.tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code, this.props.mappedRoomTiming)}</span> : null}
            {room.roomType && (
              <span>
                {room.roomType.onlineFlag
                  ? this.props.t('common.labels.online')
                  : this.props.t('common.labels.paper')}
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="">
            {completedSeatingDetailsFlag && (
              <>
                <h5>
                  {this.props.t(
                    isRoomOnline(room.roomType)
                      ? 'seatingChart.subtitleOnline'
                      : 'seatingChart.subtitle'
                  )}
                </h5>
                <div className="dark-gray mb-3">{this.props.t('seatingChart.description')}</div>
                <div className="border-box-silver">
                  <div className="row">
                    <div className="row-title col">
                      <input
                        type="radio"
                        id="seats-examinees"
                        value="0"
                        onChange={(e) => this.handleChangeMaterialType(e)}
                        checked={this.state.viewMaterialType == 0}
                      />
                      <label htmlFor="seats-examinees">
                        {this.props.t('seatingChart.seatsExamineesLabel')}
                      </label>
                    </div>
                    <div className="row-content col">
                      <span>
                        {this.props.t('seatingChart.countSeatsSelected', {
                          count: regSummaries.flat().filter(Boolean).length,
                          total: allStudents.length,
                        })}
                      </span>
                    </div>
                    <div className="row-action col">
                      <Link
                        className={`btn btn-small ${
                          isEmpty(regSummaries) ? 'btn-primary' : 'btn-outline'
                        }`}
                        to={`${basePath}/seats-in-use`}>
                        {isEmpty(regSummaries)
                          ? this.props.t('common.labels.assign')
                          : this.props.t('common.labels.edit')}
                      </Link>
                    </div>
                  </div>

                  {!!roomTypeMaterialTypes.length &&
                    roomTypeMaterialTypes.map((material) => {
                      let ranges = filter(
                        roomsRanges,
                        (range) => range.materialTypeId === material.id
                      );
                      let usedBooklets =
                        regSummaries &&
                        filter(
                          regSummaries.flat().filter(Boolean),
                          (cell) =>
                            cell?.regMaterials?.length &&
                            some(cell.regMaterials, (m) => m.materialTypeId == material.id)
                        ).length;
                      return (
                        <div className="row" key={material.id}>
                          <div className="row-title col">
                            <input
                              type="radio"
                              id={`material-${material.id}`}
                              value={material.id}
                              onChange={(e) => this.handleChangeMaterialType(e)}
                              checked={this.state.viewMaterialType == material.id}
                            />
                            <label htmlFor={`material-${material.id}`}>
                              {material.name} (
                              {this.props.t('testCenter.testMaterials.usedOf', {
                                count: usedBooklets,
                                total: sumBy(ranges, 'count'),
                              })}
                              )
                              <i
                                className={`fa fa-circle color-material-type-${str2ClassName(
                                  material.name
                                )}`}></i>
                            </label>
                          </div>
                          <div className="row-content col">
                            {ranges.length ? (
                              <React.Fragment>
                                <div className="inline-block">
                                  {this.props.t('seatingChart.rangeLabel')}
                                </div>
                                <div className="inline-block">
                                  {ranges.map((range) => (
                                    <div key={range.id}>
                                      {range.rangeBegin} - {range.rangeEnd}
                                    </div>
                                  ))}
                                </div>
                              </React.Fragment>
                            ) : (
                              this.props.t('seatingChart.noTestBooklets')
                            )}
                          </div>
                          <div className="row-action col">
                            <Link
                              className={`btn btn-small ${
                                usedBooklets ? 'btn-outline' : 'btn-primary'
                              } ${isEmpty(regSummaries) || !ranges.length ? 'disabled' : null}`}
                              to={`${basePath}/${btnAction}/${material.id}`}
                              disabled={isEmpty(regSummaries) ? 'disabled' : null}>
                              {this.props.t(
                                usedBooklets ? 'common.labels.edit' : 'common.labels.assign'
                              )}
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </>
            )}

            <div className="edit-seating">
              <span>
                {this.props.t('seatingChart.rowsColumns', {
                  rows: room.seatingRowCount,
                  columns: room.seatingColumnCount,
                })}
              </span>
              <button className="btn-link" onClick={this.toggleEditSeatModal}>
                <PencilIcon />
                <span className="hide-mobile">{this.props.t('seatingChart.editSeatingBtn')}</span>
                <span className="show-mobile">
                  {this.props.t('seatingChart.editSeatingBtnMobile')}
                </span>
              </button>
            </div>
          </div>

          {completedSeatingDetailsFlag && (
            <div className=" pos-relative" ref={this.seatingChart}>
              <SeatingChartGrid
                {...gridProps}
                rows={room.seatingRowCount}
                cols={room.seatingColumnCount}
                seatsData={regSummaries}
                roomTypeMaterialTypes={roomTypeMaterialTypes}
                seatingChart={this.seatingChart}
              />
              {isEmpty(regSummaries) && (
                <div className="select-seats" style={btnStyle}>
                  <Link className="btn btn-primary" to={`${basePath}/seats-in-use`}>
                    {this.props.t('seatingChart.selectSeatsBtn2')}
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="container btn-back-bar">
          <button className="btn btn-outline ml-0 btn-back" onClick={this.props.history.goBack}>
            {this.props.t('common.labels.back')}
          </button>
        </div>

        <ModalSeatingDetails
          room={room}
          roomStatus={roomStatus}
          modalOpen={modalEditSeatsOpen}
          closeModal={this.toggleEditSeatModal}
          regSummaries={regSummaries}
          tcEvent={tcEvent}
        />

        <AlertMessage
          show={showAlertMessage}
          removeMessage={this.removeAlertMessage}
          message={alertMessage}
          time={ALERT_MESSAGE_TIME}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    roomStatus: state.taskManagement.roomStatus,
    mappedRoomTiming: state.statics.mappedRoomTiming,
    tcEvent: state.tcEvent.currentEvent || {},
    allStudents: getAllStudents(state),
    configValues: state.statics.configValues,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...taskActions,
        ...tcEventActions,
        ...seatingChartActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(SeatingChartDashboard)
);

SeatingChartDashboard.propTypes = {
  t: PropTypes.func.isRequired,
  // Props from parent component / react-router
  location: PropTypes.shape({
    state: PropTypes.shape({
      successNotif: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  eventId: PropTypes.number.isRequired,
  roomId: PropTypes.number.isRequired,
  room: PropTypes.shape({
    name: PropTypes.string,
    seatingRowCount: PropTypes.number,
    seatingColumnCount: PropTypes.number,
    writingSurfaceWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    writingSurfaceDepth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    distanceBetweenShoulders: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    distanceBetweenHeads: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roomType: PropTypes.shape({
      name: PropTypes.string,
      onlineFlag: PropTypes.bool,
    }),
    timing: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  roomTypeMaterialTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ).isRequired,
  roomsRanges: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      materialTypeId: PropTypes.number,
      count: PropTypes.number,
      rangeBegin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      rangeEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  regSummaries: PropTypes.array.isRequired,

  // Props from mapStateToProps
  roomStatus: PropTypes.shape({
    testCenterName: PropTypes.string,
    locationName: PropTypes.string,
    locationArea: PropTypes.string,
  }).isRequired,
  mappedRoomTiming: PropTypes.object.isRequired,
  tcEvent: PropTypes.shape({
    testingEvent: PropTypes.shape({
      name: PropTypes.string,
    }),
    equatingFlag: PropTypes.bool,
  }),
  allStudents: PropTypes.array,
  configValues: PropTypes.arrayOf(PropTypes.shape({})),

  // Props from mapDispatchToProps
  actions: PropTypes.shape({
    getRoomStatus: PropTypes.func.isRequired,
    getTcEvent: PropTypes.func.isRequired,
    getStudentsList: PropTypes.func.isRequired,
  }).isRequired,
};

SeatingChartDashboard.defaultProps = {
  allStudents: [],
  configValues: [],
};
